import imageCompression from 'browser-image-compression';

const FFMPEG_CORE_BASE_URL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';

// Preferred file extension per output MIME type. Used to keep the uploaded
// filename's extension in sync with the actual optimized output format so
// the backend can serve it with the right Content-Type.
const EXT_BY_MIME = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/bmp': 'bmp',
  'image/avif': 'avif',
  'video/mp4': 'mp4',
  'video/webm': 'webm',
  'application/pdf': 'pdf',
};

// Compute the output filename for an optimized file. Same-format output
// keeps the original name untouched (extension included); when the format
// changed (e.g. video -> mp4) or the name has no extension, the extension
// is replaced/appended to match the actual output type.
function nameForOutput(originalName, originalType, outputType) {
  const sameFormat = !outputType || outputType === originalType;
  if (sameFormat && /\.[^/.]+$/.test(originalName)) return originalName;
  const ext = EXT_BY_MIME[outputType || originalType];
  if (!ext) return originalName;
  const base = String(originalName).replace(/\.[^/.]+$/, '');
  return `${base}.${ext}`;
}

let ffmpegInstance = null;

async function getFFmpeg() {
  if (ffmpegInstance) return ffmpegInstance;
  const { FFmpeg } = await import('@ffmpeg/ffmpeg');
  const { fetchFile, toBlobURL } = await import('@ffmpeg/util');
  const ffmpeg = new FFmpeg();
  const coreURL = await toBlobURL(`${FFMPEG_CORE_BASE_URL}/ffmpeg-core.js`, { type: 'text/javascript' });
  const wasmURL = await toBlobURL(`${FFMPEG_CORE_BASE_URL}/ffmpeg-core.wasm`, { type: 'application/wasm' });
  await ffmpeg.load({ coreURL, wasmURL });
  ffmpegInstance = ffmpeg;
  return ffmpeg;
}

export async function optimizeImage(file, { maxWidth = 1600, maxHeight = 1600, quality = 0.8, maxSizeMB = 4 } = {}) {
  try {
    const compressed = await imageCompression(file, {
      maxSizeMB,
      maxWidthOrMaxHeight: Math.max(maxWidth, maxHeight),
      useWebWorker: true,
      initialQuality: quality,
    });
    if (!compressed) return file;
    const outType = compressed.type || file.type;
    const outName = nameForOutput(file.name, file.type, outType);
    if (compressed.name === outName && compressed.type === outType) return compressed;
    return new File([compressed], outName, { type: outType });
  } catch (error) {
    console.error('optimizeImage error:', error);
    return file;
  }
}

export async function optimizeVideo(file, { onProgress } = {}) {
  if (!file || !file.type || !file.type.startsWith('video/')) return file;
  try {
    const ffmpeg = await getFFmpeg();
    const ext = (file.name.split('.').pop() || 'mp4').toLowerCase();
    const inputName = `input.${ext}`;
    const outputName = 'output.mp4';
    const { fetchFile } = await import('@ffmpeg/util');
    if (typeof onProgress === 'function') {
      ffmpeg.on('progress', onProgress);
    }
    await ffmpeg.writeFile(inputName, await fetchFile(file));
    await ffmpeg.exec([
      '-i', inputName,
      '-c:v', 'libx264',
      '-crf', '28',
      '-preset', 'veryfast',
      '-c:a', 'aac',
      '-b:a', '128k',
      '-movflags', '+faststart',
      outputName,
    ]);
    const data = await ffmpeg.readFile(outputName);
    const outName = nameForOutput(file.name, file.type, 'video/mp4');
    const optimized = new File([data], outName, { type: 'video/mp4' });
    try {
      ffmpeg.deleteFile(inputName);
      ffmpeg.deleteFile(outputName);
    } catch (cleanupErr) {
      console.error('optimizeVideo cleanup error:', cleanupErr);
    }
    return optimized;
  } catch (error) {
    console.error('optimizeVideo error:', error);
    return file;
  }
}

export async function optimizePdf(file) {
  if (!file || file.type !== 'application/pdf') return file;
  try {
    const { PDFDocument } = await import('pdf-lib');
    const pdf = await PDFDocument.load(await file.arrayBuffer());
    const bytes = await pdf.save({ useObjectStreams: true });
    return new File([bytes], nameForOutput(file.name, file.type, 'application/pdf'), { type: 'application/pdf' });
  } catch (error) {
    console.error('optimizePdf error:', error);
    return file;
  }
}

export async function optimizeFile(file, { onProgress } = {}) {
  if (!file) return file;
  if (file.type && file.type.startsWith('image/')) return optimizeImage(file);
  if (file.type && file.type.startsWith('video/')) return optimizeVideo(file, { onProgress });
  if (file.type === 'application/pdf') return optimizePdf(file);
  return file;
}
